import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@shared/ui/Button';
import { Card, CardTitle } from '@shared/ui/Card';
import { Input } from '@shared/ui/Input';
import {
  useAddKadryTariffMutation,
  useCreateKadryBudgetMutation,
  useListKadryBudgetsQuery,
  useListKadryGatesQuery,
  useListKadryProcessingProfilesQuery,
  useListKadryTariffsQuery,
  useSetKadryBudgetLimitMutation,
  useSetKadryProcessingProfileStatusMutation,
  useUpdateKadryProcessingProfileMutation,
} from '@shared/api';
import { kadryMoney, type KadryProcessingProfile } from '@entities/kadry';
import { KadryCardHead, KadryErrorBanner, KadryFieldGrid, KadryFieldHint, KadryMuted } from '../ui';
import {
  ApproveForm,
  CardBody,
  Checklist,
  Line,
  Mark,
  monthRef,
  PROFILES,
  REQUIRED_GATES,
  Row,
  RowAction,
  SOURCE_UI,
  Step,
  type KadrySourceId,
} from './connectionsShared';

export function ProfileApprove({ p }: { p: KadryProcessingProfile }) {
  const [update, updateState] = useUpdateKadryProcessingProfileMutation();
  const [setStatus, statusState] = useSetKadryProcessingProfileStatusMutation();
  const [open, setOpen] = useState(false);
  const [basis, setBasis] = useState(p.legal_basis);
  const [decision, setDecision] = useState(p.decision_ref ?? '');
  const [owner, setOwner] = useState(p.owner ?? '');

  if (!open) {
    return (
      <RowAction>
        <Button type="button" $size="sm" $variant="secondary" onClick={() => setOpen(true)}>
          Утвердить
        </Button>
      </RowAction>
    );
  }
  return (
    <ApproveForm
      onSubmit={async (e) => {
        e.preventDefault();
        try {
          const upd = await update({
            id: p.id,
            version: p.version,
            body: {
              country: p.country,
              fields: p.fields,
              legal_basis: basis.trim(),
              decision_ref: decision.trim() || null,
              owner: owner.trim() || null,
              no_retention: p.no_retention,
              cross_border_notice_ref: p.cross_border_notice_ref,
            },
          }).unwrap();
          await setStatus({ id: upd.id, version: upd.version, status: 'approved' }).unwrap();
          setOpen(false);
        } catch {
          /* ошибка ниже */
        }
      }}
    >
      <KadryFieldGrid $cols={1}>
        <Input
          value={basis}
          onChange={(e) => setBasis(e.target.value)}
          placeholder="Правовое основание передачи"
          aria-label="Правовое основание"
        />
        <Input
          value={decision}
          onChange={(e) => setDecision(e.target.value)}
          placeholder="Ссылка на решение (приказ, заключение юриста)"
          aria-label="Ссылка на решение"
        />
        <Input
          value={owner}
          onChange={(e) => setOwner(e.target.value)}
          placeholder="Ответственный"
          aria-label="Ответственный"
        />
      </KadryFieldGrid>
      <KadryFieldHint>
        Передаваемые поля:{' '}
        {Object.entries(p.fields)
          .map(([op, f]) => `${op}: ${f.join(', ')}`)
          .join('; ') || '—'}
      </KadryFieldHint>
      <Line>
        <Button
          type="submit"
          $size="sm"
          disabled={!basis.trim() || !decision.trim() || updateState.isLoading || statusState.isLoading}
        >
          Утвердить профиль
        </Button>
        <Button type="button" $size="sm" $variant="ghost" onClick={() => setOpen(false)}>
          Отмена
        </Button>
      </Line>
      {updateState.error ? <KadryErrorBanner error={updateState.error} /> : null}
      {statusState.error ? <KadryErrorBanner error={statusState.error} /> : null}
    </ApproveForm>
  );
}

/** Цена платного действия и бюджет площадки на текущий месяц. */
export function MoneyBlock({
  source,
  action,
  priceLabel,
  priceHint,
}: {
  source: KadrySourceId;
  action: 'platform_invite' | 'open_contacts';
  priceLabel: string;
  priceHint: string;
}) {
  const title = SOURCE_UI[source].title;
  const tariffs = useListKadryTariffsQuery();
  const budgets = useListKadryBudgetsQuery();
  const [addTariff, tariffState] = useAddKadryTariffMutation();
  const [createBudget, budgetState] = useCreateKadryBudgetMutation();
  const [setLimit, limitState] = useSetKadryBudgetLimitMutation();
  const [price, setPrice] = useState('');
  const [limit, setLimitValue] = useState('');

  const now = Date.now();
  const tariff = tariffs.data?.find(
    (t) =>
      t.source === source && t.action === action && (!t.valid_to || Date.parse(t.valid_to) > now),
  );
  const month = monthRef(source);
  const budget = budgets.data?.find((b) => b.scope === 'source_month' && b.scope_ref === month);

  return (
    <Step>
      <h4>Деньги {title}</h4>
      <Checklist>
        <li>
          <Mark ok={Boolean(tariff)} /> {priceLabel}:{' '}
          {tariff?.unit_price_rub != null ? kadryMoney(tariff.unit_price_rub) : 'не задана'}
        </li>
        <li>
          <Mark ok={Boolean(budget && budget.limit_rub > 0)} /> Бюджет {title} на этот месяц:{' '}
          {budget ? `${kadryMoney(budget.limit_rub)}, потрачено ${kadryMoney(budget.spent_rub)}` : 'не задан'}
        </li>
      </Checklist>
      <Row
        onSubmit={async (e) => {
          e.preventDefault();
          const v = Number(price.replace(',', '.'));
          if (!Number.isFinite(v) || v < 0) return;
          try {
            await addTariff({
              source,
              action,
              tariff_version: `${source}-${new Date().toISOString().slice(0, 10)}`,
              rule: 'actual_unit',
              unit_price_rub: v,
            }).unwrap();
            setPrice('');
          } catch {
            /* ошибка ниже */
          }
        }}
      >
        <Input
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          placeholder={priceHint}
          aria-label={`${priceLabel}: ${title}`}
          inputMode="decimal"
        />
        <Button type="submit" $size="sm" disabled={!price || tariffState.isLoading}>
          Задать цену
        </Button>
      </Row>
      <Row
        onSubmit={async (e) => {
          e.preventDefault();
          const v = Number(limit.replace(',', '.'));
          if (!Number.isFinite(v) || v < 0) return;
          try {
            if (budget) {
              await setLimit({ id: budget.id, version: budget.version, limit_rub: v }).unwrap();
            } else {
              await createBudget({ scope: 'source_month', scope_ref: month, limit_rub: v }).unwrap();
            }
            setLimitValue('');
          } catch {
            /* ошибка ниже */
          }
        }}
      >
        <Input
          value={limit}
          onChange={(e) => setLimitValue(e.target.value)}
          placeholder={`Бюджет ${title} на месяц, ₽`}
          aria-label={`Бюджет на месяц: ${title}`}
          inputMode="decimal"
        />
        <Button type="submit" $size="sm" disabled={!limit || budgetState.isLoading || limitState.isLoading}>
          {budget ? 'Изменить бюджет' : 'Задать бюджет'}
        </Button>
      </Row>
      {[tariffState.error, budgetState.error, limitState.error].map((err, i) =>
        err ? <KadryErrorBanner key={i} error={err} /> : null,
      )}
    </Step>
  );
}

export function BudgetsCard() {
  const gates = useListKadryGatesQuery();
  const profiles = useListKadryProcessingProfilesQuery();

  return (
    <Card>
      <KadryCardHead>
        <CardTitle>Готовность к работе</CardTitle>
      </KadryCardHead>
      <CardBody>
        <KadryMuted>
          До приёмки модуль работает только на тестовых данных. Каждое включение фиксируется в журнале.
        </KadryMuted>

        <Step>
          <h4>Функции</h4>
          <Checklist>
            {REQUIRED_GATES.map((g) => {
              const st = gates.data?.find((x) => x.gate === g.gate);
              return (
                <li key={g.gate}>
                  <Mark ok={Boolean(st?.enabled)} /> {g.label}
                </li>
              );
            })}
          </Checklist>
          <KadryMuted>
            Включаются в разделе <Link to="/agents/kadry/settings/gates">«Функции»</Link>.
          </KadryMuted>
        </Step>

        <Step>
          <h4>Профили обработки ПДн</h4>
          <Checklist>
            {PROFILES.map((x) => {
              const p = profiles.data?.find((pp) => pp.recipient === x.recipient);
              return (
                <li key={x.recipient}>
                  <Mark ok={p?.status === 'approved'} />
                  <span>{x.label}</span>
                  {p && p.status !== 'approved' && <ProfileApprove p={p} />}
                </li>
              );
            })}
          </Checklist>
          {profiles.error ? <KadryErrorBanner error={profiles.error} onRefresh={profiles.refetch} /> : null}
        </Step>

        <MoneyBlock
          source="hh"
          action="platform_invite"
          priceLabel="Цена приглашения из поиска (контакт hh)"
          priceHint="Цена одного приглашения, ₽"
        />
        <MoneyBlock
          source="rabota"
          action="open_contacts"
          priceLabel="Цена открытия контактов (после ответа «Подходит»)"
          priceHint="Цена открытия одного контакта, ₽"
        />
      </CardBody>
    </Card>
  );
}

export { BudgetsCard as ReadinessCard };
